/**
 * Async Invite actions
 */
import {
  ANNOUNCEMENT_TYPES,
  DECLINE_INVITE_ANNOUNCE_MAP,
  INVITE_STATUSES,
  INVITE_TYPES,
  VIDEO_MEMBER_TYPES,
  WSS_MESSAGE_TYPES
} from "@/helpers/constants";
import awsRestAPI from "@/services/api/awsRestAPI";
import { getUUID } from "@/helpers/helpers";

export default {
  /**
   * Get User's invites
   * @return {Promise<void>}
   */
  async getInvites({ commit, dispatch }) {
    let response = await awsRestAPI.getInvites();
    let { sentInvites, receivedInvites } = response.data;

    // Decline all old VideoCall invites - it should be actual only for active session (not after page reloading)
    let videoCallInvites = sentInvites.filter(invite => invite.inviteType === INVITE_TYPES.VIDEO_CALL);
    videoCallInvites.concat(receivedInvites.filter(invite => invite.inviteType === INVITE_TYPES.VIDEO_CALL));
    for (const invite of videoCallInvites) {
      await dispatch("updateInvite", {
        invite: invite,
        data: { inviteStatus: INVITE_STATUSES.DECLINE_BY_TIMEOUT }
      });
    }

    commit("setInvites", {
      sentInvites: sentInvites.filter(invite => invite.inviteType !== INVITE_TYPES.VIDEO_CALL),
      receivedInvites: receivedInvites.filter(invite => invite.inviteType !== INVITE_TYPES.VIDEO_CALL)
    });

    return response.data;
  },

  /**
   * Create invite (for table movement, video call, etc)
   * It's one-to-one message (working as private messages)
   * @param state {Object}
   * @param dispatch {Function}
   * @param commit {Function}
   * @param inviteType {String}
   * @param groupUUID {String}
   * @param userUUID {String}
   * @param data {Object}
   */
  async createInvite({ dispatch, commit, rootState }, { inviteType, groupUUID, userUUID, data }) {
    let payload = {
      uuid: getUUID(),
      messageType: WSS_MESSAGE_TYPES.INVITE,
      inviterUUID: rootState.users.user.user_uuid,
      inviteeUUID: userUUID,
      groupUUID: groupUUID,
      inviteType: inviteType,
      messageBody: data,
      inviteStatus: INVITE_STATUSES.SEND
    };

    await dispatch("socket/sendToSocket", payload, { root: true });

    commit("addSentInvite", payload);

    return payload;
  },

  /**
   * Create table movement invite
   * @param getters {Object}
   * @param dispatch {Function}
   * @param userUUID {String}
   */
  async createTableMovementInvite({ dispatch, rootGetters }, { userUUID }) {
    let user = rootGetters["users/getUser"](userUUID);

    let payload = {
      inviteType: INVITE_TYPES.TABLE_MOVEMENT,
      groupUUID: rootGetters["tables/userTable"].uuid,
      userUUID: userUUID,
      data: {
        tableUUID: rootGetters["tables/userTable"].TABLE_UUID,
        tableContentNodeUUID: rootGetters["tables/userTable"].uuid,
        tableName: rootGetters["tables/userTable"].TITLE,
        inviterFullName: rootGetters["users/userFullName"],
        inviteeFullName: rootGetters["users/getUserFullName"](user)
      }
    };

    return await dispatch("createInvite", payload);
  },

  /**
   * Create video call invite
   * @param rootGetters {Object}
   * @param dispatch {Function}
   * @param videoChatUUID {String}
   * @param userUUID {String}
   */
  async createVideoCallInvite({ dispatch, rootGetters }, { videoChatUUID, userUUID }) {
    let user = rootGetters["users/getUser"](userUUID);
    let members = rootGetters["videoChat/videoChatMembers"]
      // Get only subscribers or invitee that already accept invite but not connect to the video session
      .filter(member => {
        return (
          member.userUUID !== userUUID &&
          (member.memberType !== VIDEO_MEMBER_TYPES.INVITEE ||
            (member.memberType === VIDEO_MEMBER_TYPES.INVITEE && member.invite.inviteStatus === INVITE_STATUSES.ACCEPT))
        );
      })
      .map(member => member.userUUID);
    let payload = {
      inviteType: INVITE_TYPES.VIDEO_CALL,
      groupUUID: videoChatUUID,
      userUUID: userUUID,
      data: {
        tableUUID: rootGetters["tables/userTable"].TABLE_UUID,
        tableContentNodeUUID: rootGetters["tables/userTable"].uuid,
        tableName: rootGetters["tables/userTable"].TITLE,
        inviterFullName: rootGetters["users/userFullName"],
        inviteeFullName: rootGetters["users/getUserFullName"](user),
        members: members.length ? members : null
      }
    };

    await dispatch("users/createUserConnection", { targetUserUUID: userUUID }, { root: true });

    return await dispatch("createInvite", payload);
  },

  /**
   * Update invite
   * @param dispatch {Function}
   * @param invite {Object}
   * @param data {Object}
   */
  updateInvite({ dispatch }, { invite, data }) {
    dispatch("socket/sendToSocket", Object.assign(invite, data), { root: true });
  },

  /**
   * Process received invite
   * @param commit {Function}
   * @param dispatch {Function}
   * @param invite {Object}
   */
  async processReceivedInvite({ commit, dispatch }, invite) {
    commit("addReceivedInvite", invite);
    if (invite.inviteType === INVITE_TYPES.VIDEO_CALL) {
      await dispatch(
        "users/createUserConnection",
        {
          targetUserUUID: invite.inviterUUID
        },
        { root: true }
      );
    }
  },

  /**
   * Process accepted invite
   * @param dispatch {Function}
   * @param invite {Object}
   */
  processAcceptedInvite({ dispatch }, invite) {
    if (invite.inviteType === INVITE_TYPES.TABLE_MOVEMENT) {
      dispatch("processTableMovementAcceptedInvite", invite);
    } else if (invite.inviteType === INVITE_TYPES.VIDEO_CALL) {
      dispatch("processVideoCallAcceptedInvite", invite);
    }
  },

  /**
   * Process TableMovement accepted invite
   * @param commit {Function}
   * @param invite {Object}
   */
  processTableMovementAcceptedInvite({ commit }, invite) {
    commit(
      "socket/showAnnounce",
      {
        message: invite,
        announceType: ANNOUNCEMENT_TYPES.TABLE_MOVEMENT_INVITE_ACCEPT
      },
      { root: true }
    );
    commit("deleteSentInvite", invite);
  },

  /**
   * Process VideoCall accepted invite
   * @param dispatch {Function}
   * @param commit {Function}
   * @param invite {Object}
   */
  async processVideoCallAcceptedInvite({ dispatch, commit }, invite) {
    dispatch(
      "videoChat/logVideoChatsAction",
      {
        actionName: "GET_VIDEO_CALL_INVITE_ACCEPT_MSG"
      },
      { root: true }
    );
    commit("updateSentInvite", invite);
    commit(
      "videoChat/setVideoChatMember",
      {
        userUUID: invite.inviteeUUID,
        data: {
          memberType: VIDEO_MEMBER_TYPES.INVITEE,
          invite: invite
        }
      },
      { root: true }
    );
    await dispatch(
      "videoChat/joinVideoCall",
      {
        videoChatUUID: invite.groupUUID
      },
      { root: true }
    );
  },

  /**
   * Process declined invite
   * @param dispatch {Function}
   * @param invite {Object}
   */
  async processDeclinedInvite({ dispatch }, invite) {
    if (invite.inviteType === INVITE_TYPES.TABLE_MOVEMENT) {
      await dispatch("processTableMovementDeclinedInvite", invite);
    } else if (invite.inviteType === INVITE_TYPES.VIDEO_CALL) {
      await dispatch("processVideoCallDeclinedInvite", invite);
    }
  },

  /**
   * Process TableMovement declined invite
   * @param commit {Function}
   * @param invite {Object}
   */
  processTableMovementDeclinedInvite({ commit }, invite) {
    commit(
      "socket/showAnnounce",
      {
        message: invite,
        announceType: DECLINE_INVITE_ANNOUNCE_MAP[invite.inviteType]
      },
      { root: true }
    );
    commit("deleteSentInvite", invite);
  },

  /**
   * Process VideoCall declined invite
   * @param state{Object}
   * @param dispatch {Function}
   * @param commit {Function}
   * @param getters {Object}
   * @param invite {Object}
   */
  async processVideoCallDeclinedInvite({ dispatch, commit, getters, rootState, rootGetters }, invite) {
    dispatch(
      "videoChat/logVideoChatsAction",
      {
        actionName: `GET_VIDEO_CALL_INVITE_${invite.inviteStatus}_MSG`
      },
      { root: true }
    );

    if (invite.inviteStatus === INVITE_STATUSES.DECLINE_BY_INVITER) {
      commit("deleteDisplayingInvite", invite);
      commit("deleteReceivedInvite", invite);
    } else {
      commit(
        "socket/showAnnounce",
        {
          message: invite,
          announceType: DECLINE_INVITE_ANNOUNCE_MAP[invite.inviteType]
        },
        { root: true }
      );
      commit("videoChat/deleteVideoChatMember", invite.inviteeUUID, { root: true });
      commit("deleteSentInvite", invite);

      if (!rootGetters["videoChat/activeVideoChat"] && !getters.sentVideoCallInvites.length) {
        commit("videoChat/setVideoCallInitiator", null, { root: true });
      }

      if (rootGetters["videoChat/activeVideoChat"] && rootState.videoChat.videoChatMembers.length === 1) {
        await dispatch("videoChat/closeActiveVideoSession", { root: true });
      }
    }
  },

  /**
   * Cancel video call invite
   * @param dispatch {Function}
   * @param commit {Function}
   * @param invite {Object} Invite instance
   * @return {Promise<void>}
   */
  async cancelVideoCallInvite({ dispatch, commit }, invite) {
    await dispatch("updateInvite", {
      invite: invite,
      data: {
        inviteStatus: INVITE_STATUSES.DECLINE_BY_INVITER
      }
    });

    commit("videoChat/deleteVideoChatMember", invite.inviteeUUID, { root: true });
    commit("deleteSentInvite", invite);
  },

  /**
   * Cancel video call invites
   * @param dispatch {Function}
   * @param commit {Function}
   * @return {Promise<void>}
   */
  async cancelVideoCallInvites({ getters, dispatch }) {
    for (const invite of getters.sentVideoCallInvites) {
      await dispatch("cancelVideoCallInvite", invite);
    }
  }
};
